// Utility functions for color analysis and team foul warning colors

/**
 * Check if a brand color is too red and would conflict with red warning backgrounds
 * @param {string} hexColor - Hex color string (e.g., "#ff0000")
 * @returns {boolean} - True if the color is too red
 */
export const isBrandColorTooRed = (hexColor) => {
  // Convert hex to RGB
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Check if red is dominant and significantly higher than green and blue
  const redDominance = r > g && r > b && (r - Math.max(g, b)) > 50;
  
  // Convert to HSL to check hue
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  
  const max = Math.max(rNorm, gNorm, bNorm);
  const min = Math.min(rNorm, gNorm, bNorm);
  const diff = max - min;
  
  let hue = 0;
  if (diff !== 0) {
    if (max === rNorm) {
      hue = ((gNorm - bNorm) / diff) % 6;
    } else if (max === gNorm) {
      hue = (bNorm - rNorm) / diff + 2;
    } else {
      hue = (rNorm - gNorm) / diff + 4;
    }
  }
  hue = Math.round(hue * 60);
  if (hue < 0) hue += 360;
  
  // Red spectrum: 0-30 degrees or 330-360 degrees
  const isRedHue = (hue >= 0 && hue <= 30) || (hue >= 330 && hue <= 360);
  
  return redDominance || isRedHue;
};

/**
 * Get warning colors for team fouls based on brand color
 * @param {string} backgroundColor - The brand color in hex format
 * @returns {object} - Object with backgroundColor and textColor CSS classes
 */
export const getWarningColors = (backgroundColor) => {
  if (isBrandColorTooRed(backgroundColor)) {
    return {
      backgroundColor: 'bg-amber-500', // Dark yellow/orange
      textColor: 'text-white'
    };
  } else {
    return {
      backgroundColor: 'bg-red-600', // Default red
      textColor: 'text-white'
    };
  }
};

/**
 * Get warning background color class for LiveView containers
 * @param {string} backgroundColor - The brand color in hex format
 * @returns {string} - CSS class for background color
 */
export const getWarningBackgroundColor = (backgroundColor) => {
  return isBrandColorTooRed(backgroundColor) ? 'bg-amber-500' : 'bg-red-600';
};
